import React, { useState } from 'react';
import { Lock, Save, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SecuritySettings = () => {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [username, setUsername] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  React.useEffect(() => {
    const user = localStorage.getItem('adminUser');
    if (user) {
      setUsername(user);
    }
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (passwords.newPassword.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await adminApi.post('/api/admin/change-password', {
        current_password: passwords.currentPassword,
        new_password: passwords.newPassword
      });

      // Cookie will be updated by backend
      
      toast({
        title: "Mot de passe modifié !",
        description: "Votre mot de passe a été mis à jour avec succès. Veuillez vous reconnecter.",
      });

      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => {
        localStorage.removeItem('adminUser');
        window.location.href = '/admin/login';
      }, 2000);

    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de modifier le mot de passe",
        variant: "destructive"
      });
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Sécurité & Paramètres</h1>
        <p className="text-gray-400">Gérez votre compte administrateur</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Change Password */}
        <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mr-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Changer le mot de passe</h2>
              <p className="text-gray-400">Sécurisez votre compte</p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe actuel *
              </label>
              <Input
                type="password"
                value={passwords.currentPassword}
                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                required
                className="bg-gray-800 text-white border-gray-700"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nouveau mot de passe *
              </label>
              <Input
                type="password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                required
                minLength={6}
                className="bg-gray-800 text-white border-gray-700"
                placeholder="••••••••"
              />
              <p className="text-gray-500 text-xs mt-1">Minimum 6 caractères</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmer le nouveau mot de passe *
              </label>
              <Input
                type="password"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                required
                className="bg-gray-800 text-white border-gray-700"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 py-3"
            >
              <Save className="w-5 h-5 mr-2" />
              Changer le mot de passe
            </Button>
          </form>
        </div>

        {/* Account Info */}
        <div className="space-y-6">
          <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Informations du compte</h2>
                <p className="text-gray-400">Détails de votre compte admin</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Nom d&apos;utilisateur
                </label>
                <p className="text-white text-lg font-semibold">{username || 'admin'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Rôle
                </label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-orange-600 text-white">
                  Administrateur
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Dernière connexion
                </label>
                <p className="text-white">Aujourd&apos;hui</p>
              </div>
            </div>
          </div>

          {/* Security Tips */}
          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-6">
            <h3 className="text-yellow-300 font-semibold mb-3 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Conseils de sécurité
            </h3>
            <ul className="text-yellow-300 text-sm space-y-2">
              <li>• Utilisez un mot de passe fort et unique</li>
              <li>• Ne partagez jamais vos identifiants</li>
              <li>• Changez régulièrement votre mot de passe</li>
              <li>• Déconnectez-vous après chaque session</li>
              <li>• Utilisez au moins 8 caractères avec lettres, chiffres et symboles</li>
            </ul>
          </div>

          {/* Danger Zone */}
          <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-6">
            <h3 className="text-red-300 font-semibold mb-3 flex items-center">
              ⚠️ Zone de danger
            </h3>
            <p className="text-red-300 text-sm mb-4">
              Après avoir changé votre mot de passe, vous serez automatiquement déconnecté et devrez vous reconnecter avec le nouveau mot de passe.
            </p>
            <p className="text-red-400 text-xs font-semibold">
              Important : Notez bien votre nouveau mot de passe avant de le modifier !
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
